/**
 * DatePicker extended component customization for WeekPicker functionality.
 * This component is extracted from the demos from official docs of @materialui/picker
 * ref: https://material-ui-pickers.dev/demo/datepicker#customization
 * Updates:
 * Added a wrapper of <MuiPickersUtilsProvider> to tackle provider error.
 * @author: "shaurabh.shah.prtnr@ironmountain.com"
 *
 */

import DateFnsUtils from "@date-io/date-fns";
import { IconButton, withStyles } from "@material-ui/core";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { createStyles } from "@material-ui/styles";
import clsx from "clsx";
import endOfWeek from "date-fns/endOfWeek";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isValid from "date-fns/isValid";
import isWithinInterval from "date-fns/isWithinInterval";
import startOfWeek from "date-fns/startOfWeek";
import moment from "moment";
import React, { PureComponent } from "react";
import { useTranslation } from "react-i18next";

class WeekPicker extends PureComponent {
  state = {
    selectedDate: this.props.startDate ? this.props.startDate : new Date(),
  };

  handleWeekChange = (date) => {
    const weekStart = startOfWeek(date);
    this.setState({ selectedDate: weekStart });
    if (this.props.weekChangeHandler) this.props.weekChangeHandler(weekStart);
  };

  formatWeekSelectLabel = (date, invalidLabel) => {
    let dateClone = date;
    const { t, i18n } = useTranslation();
    return dateClone && isValid(dateClone)
      ? `${t("pgReview.lbWeekof")} ${format(startOfWeek(dateClone), "MMM do")}`
      : invalidLabel;
  };

  renderWrappedWeekDay = (date, selectedDate, dayInCurrentMonth) => {
    const { classes } = this.props;
    let dateClone = date;
    let selectedDateClone = selectedDate;

    const start = startOfWeek(selectedDateClone);
    const end = endOfWeek(selectedDateClone);

    const dayIsBetween = isWithinInterval(dateClone, { start, end });
    const isFirstDay = isSameDay(dateClone, start);
    const isLastDay = isSameDay(dateClone, end);

    const wrapperClassName = clsx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = clsx(classes.day, {
      [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
      [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
    });

    const disabled =
      this.props.minDate &&
      this.props.maxDate &&
      (moment(date).diff(startOfWeek(new Date(this.props.minDate))) < 0 ||
        moment(date).diff(endOfWeek(new Date(this.props.maxDate)))) > 0
        ? true
        : false;

    return (
      <div className={wrapperClassName}>
        <IconButton className={dayClassName} disabled={disabled}>
          <span> {format(dateClone, "d")} </span>
        </IconButton>
      </div>
    );
  };

  render() {
    const { selectedDate } = this.state;

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <DatePicker
          minDate={
            this.props.minDate && startOfWeek(new Date(this.props.minDate))
          }
          maxDate={
            this.props.maxDate && endOfWeek(new Date(this.props.maxDate))
          }
          value={selectedDate}
          onChange={this.handleWeekChange}
          TextFieldComponent={this.props.renderInput}
          renderDay={this.renderWrappedWeekDay}
          labelFunc={this.formatWeekSelectLabel}
        />
      </MuiPickersUtilsProvider>
    );
  }
}

const styles = createStyles((theme) => ({
  dayWrapper: {
    position: "relative",
  },
  day: {
    width: 36,
    height: 36,
    fontSize: theme.typography.caption.fontSize,
    margin: "0 2px",
    color: "inherit",
  },
  customDayHighlight: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "2px",
    right: "2px",
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: "50%",
  },
  nonCurrentMonthDay: {
    color: theme.palette.text.disabled,
  },
  highlightNonCurrentMonthDay: {
    color: "#676767",
  },
  highlight: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  firstHighlight: {
    extend: "highlight",
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  },
  endHighlight: {
    extend: "highlight",
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  },
}));

export default withStyles(styles)(WeekPicker);
